import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { appContext } from 'common/contexts';
import { Routes as R } from 'common/enums';
import { PlusTwoTone, TrashCanOutlineTwoTone, ArrowLeftTwoTone } from 'common/icons';
import { Recipe, Ingredient, InventoryItem, Item } from 'common/types';
import styles from './recipeEditor.module.css';

type RecipeEditorParams = {
  id?: string;
};

const RecipeEditor: React.FC = () => {
  const { id } = useParams<RecipeEditorParams>();
  const history = useHistory();
  const { state, actions, helpers } = useContext(appContext);
  const { recipes = [], inventoryItems = [], items = [] } = state;
  const { translation } = helpers;
  const { t } = translation;
  
  const isEditMode = !!id;
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Filter active items and inventory items
  const activeItems = items.filter(item => !item.isDeleted);
  const activeInventoryItems = inventoryItems.filter(item => !item.isDeleted && item.type === 'raw');

  // Form state
  const [formData, setFormData] = useState<Omit<Recipe, 'id' | 'ownerId' | 'parentUserId'>>({ 
    productId: '',
    ingredients: []
  });

  // Load recipe data if in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      const recipe = recipes.find(recipe => recipe.id === id);
      if (recipe) {
        setFormData({
          productId: recipe.productId,
          ingredients: [...recipe.ingredients]
        });
      } else {
        // Recipe not found, redirect to list
        history.push(R.RecipesList);
      }
    }
  }, [isEditMode, id, recipes, history]);

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!formData.productId) {
      errors.productId = t('common.errors.required');
    }
    
    if (formData.ingredients.length === 0) {
      errors.ingredients = t('admin.inventory.recipeEditor.noIngredientsError');
    }
    
    // Check for duplicate ingredients
    const ingredientIds = formData.ingredients.map(ing => ing.inventoryItemId);
    const hasDuplicates = ingredientIds.some((id, index) => ingredientIds.indexOf(id) !== index);
    
    if (hasDuplicates) {
      errors.ingredients = t('admin.inventory.recipeEditor.duplicateIngredientsError');
    }
    
    // Check for valid quantities
    const hasInvalidQuantities = formData.ingredients.some(ing => ing.quantity <= 0);
    
    if (hasInvalidQuantities) {
      errors.ingredients = t('admin.inventory.recipeEditor.invalidQuantitiesError');
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addIngredient = () => {
    setFormData(prev => ({
      ...prev,
      ingredients: [
        ...prev.ingredients,
        { inventoryItemId: '', quantity: 0 }
      ]
    }));
  };

  const removeIngredient = (index: number) => {
    setFormData(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index)
    }));
  };

  const handleIngredientChange = (index: number, field: keyof Ingredient, value: string | number) => {
    setFormData(prev => {
      const updatedIngredients = [...prev.ingredients];
      updatedIngredients[index] = {
        ...updatedIngredients[index],
        [field]: field === 'quantity' ? Number(value) : value
      };
      return {
        ...prev,
        ingredients: updatedIngredients
      };
    });
  };

  const getInventoryItemUnit = (inventoryItemId: string) => {
    const item = inventoryItems.find(item => item.id === inventoryItemId);
    return item ? item.unit : '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      if (isEditMode && id) {
        // Update existing recipe
        await actions.inventory.updateRecipe({
          ...formData,
          id
        });
        toast.success(t('admin.inventory.recipeEditor.recipeUpdatedSuccess'));
      } else {
        // Create new recipe
        await actions.inventory.addRecipe(formData);
        toast.success(t('admin.inventory.recipeEditor.recipeCreatedSuccess'));
      }
      
      // Redirect to recipes list
      history.push(R.RecipesList);
    } catch (error) {
      console.error('Error saving recipe:', error);
      toast.error(t('admin.inventory.recipeEditor.errorSavingRecipe'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.titleContainer}>
          <Link to={R.RecipesList} className={styles.backButton}>
            <ArrowLeftTwoTone />
          </Link>
          <h1 className={styles.title}>
            {isEditMode 
              ? t('admin.inventory.recipeEditor.editRecipeTitle')
              : t('admin.inventory.recipeEditor.addRecipeTitle')}
          </h1>
        </div>
      </div>
      
      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="productId">
            {t('admin.inventory.recipeEditor.productLabel')}
          </label>
          <select
            id="productId"
            name="productId"
            className={styles.select}
            value={formData.productId}
            onChange={handleChange}
            required
          >
            <option value="">{t('common.chooseAnOption')}</option>
            {activeItems.map(item => (
              <option key={item.id} value={item.id}>{item.name}</option>
            ))}
          </select>
          {formErrors.productId && <div className={styles.error}>{formErrors.productId}</div>}
        </div>
        
        <div className={styles.ingredientsSection}>
          <div className={styles.ingredientsHeader}>
            <h2 className={styles.ingredientsTitle}>
              {t('admin.inventory.recipeEditor.ingredientsLabel')}
            </h2>
            <button 
              type="button" 
              className={styles.addIngredientButton}
              onClick={addIngredient}
            >
              <PlusTwoTone className={styles.addIngredientIcon} />
              {t('admin.inventory.recipeEditor.addIngredientLabel')}
            </button>
          </div>
          
          {formErrors.ingredients && <div className={styles.error}>{formErrors.ingredients}</div>}
          
          <div className={styles.ingredientsList}>
            {formData.ingredients.length > 0 ? (
              formData.ingredients.map((ingredient, index) => {
                const unit = getInventoryItemUnit(ingredient.inventoryItemId);
                return (
                  <div key={index} className={styles.ingredientItem}>
                    <select
                      className={`${styles.select} ${styles.ingredientSelect}`}
                      value={ingredient.inventoryItemId}
                      onChange={(e) => handleIngredientChange(index, 'inventoryItemId', e.target.value)}
                      required
                    >
                      <option value="">{t('common.chooseAnOption')}</option>
                      {activeInventoryItems.map(item => (
                        <option key={item.id} value={item.id}>{item.name}</option>
                      ))}
                    </select>
                    
                    <input
                      type="number"
                      step="0.01"
                      className={`${styles.input} ${styles.quantityInput}`}
                      value={ingredient.quantity}
                      onChange={(e) => handleIngredientChange(index, 'quantity', e.target.value)}
                      min="0.01"
                      required
                    />
                    
                    <span className={styles.unitLabel}>{unit}</span>
                    
                    <button
                      type="button"
                      className={styles.removeIngredientButton}
                      onClick={() => removeIngredient(index)}
                    >
                      <TrashCanOutlineTwoTone />
                    </button>
                  </div>
                );
              })
            ) : (
              <div className={styles.noIngredients}>
                {t('admin.inventory.recipeEditor.noIngredientsMessage')}
              </div>
            )}
          </div>
        </div>
        
        <div className={styles.buttonGroup}>
          <button
            type="submit"
            className={styles.submitButton}
            disabled={loading}
          >
            {loading 
              ? t('common.saving')
              : isEditMode 
                ? t('common.update')
                : t('common.create')}
          </button>
          
          <Link to={R.RecipesList} className={styles.cancelButton}>
            {t('common.cancel')}
          </Link>
        </div>
      </form>
    </div>
  );
};

export default RecipeEditor;
